/**
 * *Your day*'s cards — which exist, where they sit, and the one line each says
 * (FR-L5, T-6.19; ADR-0033 §4).
 *
 * Pure, so the rules are testable without mounting:
 *
 * - **A card only for a held verb.** Present when the caller holds the part's
 *   `can_view` here *and* the service answered something other than `null` —
 *   the UI half and the API half of the same check (CLAUDE.md §7). Absent,
 *   never disabled.
 * - **A failed part shows no figure.** Its line says it could not be read, in
 *   the attention tone. A zero there would be believed.
 * - **Nothing is invented while loading** — the card draws a skeleton line.
 */

import type { LucideIcon } from 'lucide-react'
import { Briefcase, CalendarCheck, CalendarDays, ListChecks, MessageSquare, Receipt, Target } from 'lucide-react'

import { BOOKING, CALENDAR, CASE, INVOICE, LEAD, TASK, type Verb } from '../../lib/access'
import type { SummaryLine } from '../../lib/api/firm'
import {
  type BookingsPart,
  type CalendarPart,
  type CasesPart,
  type InvoicesPart,
  type LeadsPart,
  type MessagesPart,
  type Overview,
  type OverviewKey,
  type TasksPart,
  partFailed,
} from '../../lib/api/overview'

export const FAILED_LINE: SummaryLine = { line: 'Couldn’t be counted just now', tone: 'attention' }

type Can = (resource: string, verb: Verb) => boolean

interface CardDef {
  key: OverviewKey
  href: string
  icon: LucideIcon
  title: string
  /** What the card counts, in the scope the server said it counted. */
  description: (part: unknown) => string
  /** Every verb the card needs here — the service's gate, mirrored. */
  needs: Array<[string, Verb]>
  line: (part: never) => SummaryLine
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

const scopeOf = (part: unknown): 'firm' | 'mine' | null =>
  typeof part === 'object' && part !== null && 'scope' in part
    ? ((part as { scope: 'firm' | 'mine' }).scope ?? null)
    : null

export function casesLine(p: CasesPart): SummaryLine {
  if (p.open === 0) return { line: 'No open cases', tone: 'unset' }
  const parts = [`${p.open} open`]
  if (p.overdue > 0) parts.push(`${p.overdue} late`)
  if (p.scope === 'firm') parts.push(`${p.mine} yours`)
  return { line: parts.join(' · '), tone: p.overdue > 0 ? 'attention' : 'set' }
}

export function tasksLine(p: TasksPart): SummaryLine {
  if (p.due_this_week === 0 && p.late === 0) return { line: 'Nothing due this week', tone: 'unset' }
  const parts = [`${p.due_today} today`, `${p.due_this_week} this week`]
  if (p.late > 0) parts.push(`${p.late} late`)
  return { line: parts.join(' · '), tone: p.late > 0 ? 'attention' : 'set' }
}

export function calendarLine(p: CalendarPart): SummaryLine {
  if (p.due_this_week === 0 && p.overdue === 0) return { line: 'Nothing due this week', tone: 'unset' }
  const parts = [`${p.due_today} today`, `${p.due_this_week} this week`]
  if (p.overdue > 0) parts.push(`${p.overdue} overdue`)
  return { line: parts.join(' · '), tone: p.overdue > 0 ? 'attention' : 'set' }
}

export function messagesLine(p: MessagesPart): SummaryLine {
  if (p.unread_conversations === 0) return { line: 'You’re all caught up', tone: 'unset' }
  return {
    line: `${plural(p.unread_conversations, 'conversation')} with ${plural(p.unread_messages, 'unread message')}`,
    tone: 'attention',
  }
}

export function invoicesLine(p: InvoicesPart): SummaryLine {
  if (p.unpaid === 0) return { line: 'Nothing unpaid', tone: 'unset' }
  const parts = [`${p.unpaid} unpaid`]
  if (p.overdue > 0) parts.push(`${p.overdue} overdue`)
  return { line: parts.join(' · '), tone: p.overdue > 0 ? 'attention' : 'set' }
}

export function leadsLine(p: LeadsPart): SummaryLine {
  if (p.new === 0 && p.follow_up_due === 0) return { line: 'No new leads', tone: 'unset' }
  return {
    line: `${p.new} new · ${p.follow_up_due} to follow up`,
    tone: p.follow_up_due > 0 ? 'attention' : 'set',
  }
}

/** w27b — C25, ADR-0052 §7: the proofs waiting for the firm. */
export function bookingsLine(p: BookingsPart): SummaryLine {
  if (p.to_check === 0) return { line: 'No payment proofs to check', tone: 'unset' }
  return { line: `${plural(p.to_check, 'payment proof')} to check`, tone: 'attention' }
}

export const CARDS: CardDef[] = [
  {
    key: 'cases',
    href: '/cases',
    icon: Briefcase,
    title: 'Cases',
    description: (part) =>
      scopeOf(part) === 'mine'
        ? 'The open cases you’re on or reviewing.'
        : 'Every open case in the firm, and how many are yours.',
    needs: [[CASE, 'can_view']],
    line: casesLine,
  },
  {
    key: 'tasks',
    href: '/workload',
    icon: ListChecks,
    title: 'Tasks',
    description: (part) =>
      scopeOf(part) === 'mine'
        ? 'Your tasks due today and in the next seven days.'
        : 'The firm’s tasks due today and in the next seven days.',
    needs: [
      [TASK, 'can_view'],
      [CASE, 'can_view'],
    ],
    line: tasksLine,
  },
  {
    key: 'calendar',
    href: '/calendar',
    icon: CalendarDays,
    title: 'Compliance calendar',
    description: () => 'Filings due today and this week, and any gone past their date.',
    needs: [[CALENDAR, 'can_view']],
    line: calendarLine,
  },
  {
    key: 'messages',
    href: '/messages',
    icon: MessageSquare,
    title: 'Messages',
    description: () => 'Conversations with something you haven’t read.',
    needs: [[CASE, 'can_view']],
    line: messagesLine,
  },
  {
    key: 'invoices',
    href: '/billing',
    icon: Receipt,
    title: 'Invoices',
    description: () => 'Issued and not yet fully paid, and the ones past their due date.',
    needs: [[INVOICE, 'can_view']],
    line: invoicesLine,
  },
  {
    key: 'leads',
    href: '/leads',
    icon: Target,
    title: 'Leads',
    description: (part) =>
      scopeOf(part) === 'mine'
        ? 'New enquiries and follow-ups due — yours and the unclaimed.'
        : 'New enquiries, and follow-ups that have come due.',
    needs: [[LEAD, 'can_view']],
    line: leadsLine,
  },
  {
    // w27b — T-6.22. Opens the list on *Proof to check*.
    key: 'bookings',
    href: '/bookings?status=payment_review',
    icon: CalendarCheck,
    title: 'Bookings',
    description: () => 'Bank-transfer bookings whose payment proof is waiting for you to check.',
    needs: [[BOOKING, 'can_view']],
    line: bookingsLine,
  },
]

export const GROUPS: Array<{ key: string; title: string; description: string; cards: OverviewKey[] }> = [
  {
    key: 'work',
    title: 'Work',
    description: 'What’s open, what’s due and who’s waiting on you.',
    cards: ['cases', 'tasks', 'calendar', 'messages'],
  },
  {
    key: 'clients-money',
    title: 'Clients and money',
    description: 'Enquiries to answer, and what clients owe the firm.',
    cards: ['leads', 'invoices', 'bookings'],
  },
]

export interface CardModel {
  key: OverviewKey
  href: string
  icon: LucideIcon
  title: string
  description: string
  /** `null` while loading — the card draws its skeleton. */
  line: SummaryLine | null
  failed: boolean
}

/**
 * The cards to draw, grouped. `data` is `null` while loading **and** when the
 * whole read failed (`failed`): in both cases the verbs held here decide which
 * cards exist, and a failed read gives each of them the failed line.
 */
export function overviewCards({
  can,
  data,
  loading,
  failed,
}: {
  can: Can
  data: Overview | null
  loading: boolean
  failed: boolean
}): Array<{ key: string; title: string; description: string; cards: CardModel[] }> {
  const models = new Map<OverviewKey, CardModel>()
  for (const card of CARDS) {
    if (!card.needs.every(([resource, verb]) => can(resource, verb))) continue
    const part = data ? data[card.key] : undefined
    // The service said the caller may not see it: no card, whatever the UI
    // thought. Only a loaded answer can say so.
    if (data && part === null) continue
    const partIsFailed = failed || partFailed(part)
    models.set(card.key, {
      key: card.key,
      href: card.href,
      icon: card.icon,
      title: card.title,
      description: card.description(partIsFailed ? null : part),
      line:
        loading && !data && !failed
          ? null
          : partIsFailed || part === undefined
            ? FAILED_LINE
            : card.line(part as never),
      failed: partIsFailed,
    })
  }
  return GROUPS.map((group) => ({
    key: group.key,
    title: group.title,
    description: group.description,
    cards: group.cards.map((key) => models.get(key)).filter((m): m is CardModel => m !== undefined),
  })).filter((group) => group.cards.length > 0)
}
